/** Any node in a CMS page model. Fields vary by componentType. */
export interface CMSNode {
  id?: string;
  componentType: string;
  [field: string]: unknown;
}

/** Contentful-style rich text node (document, paragraph, text, hyperlink, ...). */
export interface RichTextNode {
  nodeType: string;
  data?: { uri?: string; [key: string]: unknown };
  content?: RichTextNode[];
  value?: string;
  marks?: { type: string }[];
}

export interface PageModel extends CMSNode {
  metadata?: CMSNode;
  pageReference?: CMSNode;
  noIndex?: boolean;
}

export interface PageContext {
  space: string;
  pageId: string;
  isHome: boolean;
  /** Outline components that have no registered renderer. */
  debug: boolean;
  /** Cloudflare Turnstile public key for forms; unset disables the challenge */
  turnstileSiteKey?: string;
}

export interface SiteLink {
  label: string;
  href: string;
}

/** Header, footer and SEO defaults shared by every page (Payload "site" global). */
export interface SiteSettings {
  header: { navLinks: SiteLink[]; cta?: SiteLink };
  footer: {
    tagline?: string;
    email?: string;
    linkGroups: { title: string; links: SiteLink[] }[];
    legalLinks: SiteLink[];
    copyright?: string;
  };
  seo: { siteName: string; description?: string; imageUrl?: string };
}

/** Serialized into the page and read back by entry-client for hydration. */
export interface RenderContext {
  pageModel: PageModel;
  pageContext: PageContext;
  site: SiteSettings;
  /** computed on the server so hydration never disagrees around New Year */
  year: number;
  componentResolutionMetadata: {
    /** componentTypes that appear on this page */
    componentTypes: string[];
  };
}

export const RENDER_CONTEXT_KEY = "__CMS_ENGINE_RENDER_CONTEXT__";

export const isCMSNode = (value: unknown): value is CMSNode =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as CMSNode).componentType === "string";

/** External links open in a new tab, like the original bytecode-web site. */
export const isExternal = (href: string) => /^https?:\/\//.test(href);

export const isRichText = (value: unknown): value is RichTextNode =>
  typeof value === "object" &&
  value !== null &&
  (value as RichTextNode).nodeType === "document";
